import { useEffect, useState } from "react";
import { Bell, BellOff, Clock3, Sparkles } from "lucide-react";
import { currentActivitySummary, syncAccountActivity } from "./activity.ts";
import { api } from "./api.ts";
import { trackEvent } from "./analytics.tsx";
import { useLocale } from "./i18n.tsx";
import { Page, type Session } from "./Shell.tsx";

const SETTINGS_KEY = "neckmoves:reminder-settings";

type ReminderSettings = { enabled: boolean; time: string; weeklyTarget: number };

const TEXT = {
  "zh-CN": {
    eyebrow: "活动节奏", title: "把微休息放进适合你的时间里", lead: "查看这台设备上的活动节奏，并选择是否让浏览器在你设定的时间轻轻提醒。",
    sessions: "近 7 天活动次数", minutes: "近 7 天活动时间", days: "近 7 天活动天数", average: "平均每次",
    unitSessions: "次", unitMinutes: "分钟", unitDays: "天", noAverage: "还没有记录",
    insightTitle: "建议节奏", insight: (count: number, time: string) => `根据这台设备最近的使用习惯，建议每周 ${count} 次、${time} 左右提醒。`,
    useSuggestion: "使用这个建议", settingsTitle: "浏览器提醒", settingsBody: "只有你主动开启后，浏览器才会请求通知权限。关闭开关会取消推送订阅。",
    toggle: "开启活动提醒", time: "提醒时间", weekly: "每周提醒次数", times: (value: number) => `每周 ${value} 次`,
    save: "保存提醒", saving: "正在保存…", saved: "提醒已保存。你可以随时回来修改或关闭。", disabled: "提醒已关闭。",
    denied: "浏览器没有允许通知。请在浏览器的网站权限中允许通知后再试。", unsupported: "这个浏览器暂不支持后台通知。你仍然可以查看本地活动记录。",
    unavailable: "提醒服务暂时无法保存，请稍后重试。", privacy: "活动次数和时长只保存在这台设备上；通知服务只保存发送提醒所需的时间、频率、时区和浏览器推送地址，不接收摄像头、关键点或转头角度。",
    privacyAccount: "活动次数和时长会保存到你的 NeckMoves 账号并在登录设备间同步；通知服务只保存提醒偏好和各设备的推送地址，不接收摄像头、关键点或转头角度。",
  },
  en: {
    eyebrow: "Your movement rhythm", title: "Put a microbreak where it fits your day", lead: "See your activity rhythm on this device and choose whether your browser should gently remind you at a time you set.",
    sessions: "Sessions in 7 days", minutes: "Minutes in 7 days", days: "Active days in 7 days", average: "Average session",
    unitSessions: "sessions", unitMinutes: "min", unitDays: "days", noAverage: "No activity yet",
    insightTitle: "Suggested rhythm", insight: (count: number, time: string) => `Based on recent use on this device, try ${count} reminders a week at around ${time}.`,
    useSuggestion: "Use this suggestion", settingsTitle: "Browser reminders", settingsBody: "Your browser asks for notification permission only after you turn reminders on. Turning them off removes the push subscription.",
    toggle: "Turn on movement reminders", time: "Reminder time", weekly: "Reminders per week", times: (value: number) => `${value} times a week`,
    save: "Save reminders", saving: "Saving…", saved: "Your reminder is saved. You can change or turn it off at any time.", disabled: "Reminders are off.",
    denied: "Notifications are not allowed. Allow them in this site's browser permissions, then try again.", unsupported: "This browser does not support background notifications. You can still view activity saved on this device.",
    unavailable: "Reminders could not be saved. Please try again shortly.", privacy: "Session count and time stay on this device. The notification service stores only the time, frequency, time zone, and browser push address needed to send reminders; it never receives camera frames, keypoints, or head angles.",
    privacyAccount: "Session count and time are saved to your NeckMoves account and synced across signed-in devices. The notification service stores only your reminder preference and each device's push address; it never receives camera frames, keypoints, or head angles.",
  },
} as const;

function readSettings(): ReminderSettings {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "null") as Partial<ReminderSettings> | null;
    if (parsed && typeof parsed.enabled === "boolean" && /^([01]\d|2[0-3]):[0-5]\d$/.test(String(parsed.time))
      && Number.isInteger(parsed.weeklyTarget) && Number(parsed.weeklyTarget) >= 1 && Number(parsed.weeklyTarget) <= 7) {
      return parsed as ReminderSettings;
    }
  } catch {
    // Use a calm default when old browser data is malformed.
  }
  return { enabled: false, time: "15:00", weeklyTarget: 3 };
}

function applicationServerKey(value: string) {
  const padded = `${value.replaceAll("-", "+").replaceAll("_", "/")}${"=".repeat((4 - value.length % 4) % 4)}`;
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

function keyString(key: ArrayBuffer | null) {
  if (!key) return "";
  let binary = "";
  for (const byte of new Uint8Array(key)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function ReminderPage({ session }: { session: Session | null }) {
  const { locale, href } = useLocale();
  const text = TEXT[locale];
  const [summary, setSummary] = useState(currentActivitySummary);
  const [settings, setSettings] = useState<ReminderSettings>(readSettings);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState<"success" | "error" | "neutral">("neutral");
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  useEffect(() => {
    const refresh = () => setSummary(currentActivitySummary());
    window.addEventListener("neckmoves:activity-updated", refresh);
    if (session) void syncAccountActivity().catch(() => {});
    return () => window.removeEventListener("neckmoves:activity-updated", refresh);
  }, [session]);

  useEffect(() => {
    if (!session) return;
    void Promise.all([
      api<{ preference: { time: string; weeklyTarget: number } | null }>("/api/reminders/preferences"),
      supported
        ? navigator.serviceWorker.getRegistration("/").then((registration) => registration?.pushManager.getSubscription())
        : Promise.resolve(null),
    ]).then(([result, subscription]) => {
      if (!result.preference) return;
      setSettings((current) => ({
        ...current,
        enabled: Boolean(subscription),
        time: result.preference!.time,
        weeklyTarget: result.preference!.weeklyTarget,
      }));
    }).catch(() => {});
  }, [session, supported]);

  async function save() {
    setBusy(true);
    setMessage("");
    try {
      if (!settings.enabled) {
        if (supported) {
          const registration = await navigator.serviceWorker.getRegistration("/");
          const subscription = await registration?.pushManager.getSubscription();
          if (subscription) {
            await api("/api/reminders/subscription", { method: "DELETE", body: JSON.stringify({ endpoint: subscription.endpoint }) });
            await subscription.unsubscribe();
          }
        }
        window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        setMessageKind("neutral");
        setMessage(text.disabled);
        void trackEvent("reminder_settings_changed", { enabled: false, weekly_target: settings.weeklyTarget });
        return;
      }
      if (!supported) throw new Error(text.unsupported);
      const permission = await Notification.requestPermission();
      void trackEvent("notification_permission_outcome", { outcome: permission });
      if (permission !== "granted") throw new Error(text.denied);
      const config = await api<{ publicKey: string; configured: boolean }>("/api/reminders/config");
      if (!config.configured || !config.publicKey) throw new Error(text.unavailable);
      await navigator.serviceWorker.register("/reminder-sw.js", { scope: "/" });
      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(config.publicKey),
      });
      await api("/api/reminders/subscription", {
        method: "POST",
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          p256dh: keyString(subscription.getKey("p256dh")),
          auth: keyString(subscription.getKey("auth")),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          time: settings.time,
          weeklyTarget: settings.weeklyTarget,
          locale,
        }),
      });
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      setMessageKind("success");
      setMessage(text.saved);
      void trackEvent("reminder_settings_changed", { enabled: true, weekly_target: settings.weeklyTarget });
    } catch (error) {
      setMessageKind("error");
      const knownMessages: string[] = [text.unsupported, text.denied, text.unavailable];
      const known = error instanceof Error && knownMessages.includes(error.message);
      setMessage(known && error instanceof Error ? error.message : text.unavailable);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page session={session} className="reminder-page">
      <main id="main" className="reminder-shell page-shell">
        <header><p className="eyebrow">{text.eyebrow}</p><h1>{text.title}</h1><p>{text.lead}</p></header>
        <section className="activity-stats" aria-label={text.eyebrow}>
          <article><span>{text.sessions}</span><strong>{summary.sessions7d}</strong><small>{text.unitSessions}</small></article>
          <article><span>{text.minutes}</span><strong>{summary.minutes7d}</strong><small>{text.unitMinutes}</small></article>
          <article><span>{text.days}</span><strong>{summary.activeDays7d}</strong><small>{text.unitDays}</small></article>
          <article><span>{text.average}</span><strong>{summary.averageMinutes || "—"}</strong><small>{summary.averageMinutes ? text.unitMinutes : text.noAverage}</small></article>
        </section>
        <section className="rhythm-card">
          <Sparkles aria-hidden="true" /><div><h2>{text.insightTitle}</h2><p>{text.insight(summary.suggestedWeeklyTarget, summary.suggestedTime)}</p></div>
          <button className="button button-secondary" type="button" onClick={() => setSettings((value) => ({ ...value, time: summary.suggestedTime, weeklyTarget: summary.suggestedWeeklyTarget }))}>{text.useSuggestion}</button>
        </section>
        <form className="reminder-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          <header><div>{settings.enabled ? <Bell aria-hidden="true" /> : <BellOff aria-hidden="true" />}</div><div><h2>{text.settingsTitle}</h2><p>{text.settingsBody}</p></div></header>
          <label className="notification-toggle"><span>{text.toggle}</span><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings((value) => ({ ...value, enabled: event.target.checked }))} /><span aria-hidden="true" /></label>
          <fieldset disabled={!settings.enabled || busy}>
            <label><span><Clock3 aria-hidden="true" />{text.time}</span><input type="time" value={settings.time} onChange={(event) => setSettings((value) => ({ ...value, time: event.target.value }))} required /></label>
            <label><span>{text.weekly}</span><select value={settings.weeklyTarget} onChange={(event) => setSettings((value) => ({ ...value, weeklyTarget: Number(event.target.value) }))}>{[1, 2, 3, 4, 5, 6, 7].map((value) => <option key={value} value={value}>{text.times(value)}</option>)}</select></label>
          </fieldset>
          {!supported ? <div className="form-message neutral" role="status">{text.unsupported}</div> : null}
          {message ? <div className={`form-message ${messageKind}`} role={messageKind === "error" ? "alert" : "status"}>{message}</div> : null}
          <button className="button button-primary" type="submit" disabled={busy}>{busy ? text.saving : text.save}</button>
          <p className="reminder-privacy">{session ? text.privacyAccount : text.privacy}</p>
        </form>
        <a className="button button-quiet reminder-play" href={href("/play")}>{locale === "en" ? "Play a round now" : "现在开始一局"}</a>
      </main>
    </Page>
  );
}
